import * as React from 'react';
import { Control, Errors } from 'react-redux-form';
import { RouteComponentProps } from 'react-router';

import { TransactionCoordinatorViewModel } from '@models/TransactionCoordinator';
import { parsePhoneNumber } from '@common/parsers';
import {
  getDefaultErrorMessages,
  hasAlpha,
  hasValue,
  isValidCity,
  isValidEmail,
  isValidPhoneNumber,
  isValidZip
} from '@common/validators';
import { PageViewModel, OfficeSearchViewModel } from '@models/common.models';
import { Option } from 'react-select';
import { RealEstateOfficeViewModel } from '@models/RealEstateOffice';
import { TypeAheadComponent } from '@common/components/typeahead/typeahead.component';
import { CheckboxInline, Select, Breadcrumbs } from '@common/components';
import { hasNumber } from '@common/validators';
import { DynamicSelectIdentifier } from '@common/components/dynamic-select/dynamic-select-types';
import { Role, Roles } from '@models/Roles';
import { FormWrapper } from '@common/components/form-wrapper/form-wrapper.component';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface TransactionCoordinatorComponentProperties extends RouteComponentProps<{ id?: number }> {
  readonly message: string;
  readonly onSaveClick: (model: TransactionCoordinatorViewModel) => void;
  readonly getTransactionCoordinator: (id?: number) => void;
  readonly officeLookup: (query: string, id?: number) => Promise<PageViewModel<OfficeSearchViewModel>>;
  readonly updateOfficeValue: (value: number) => void;
  readonly clearTransactionCoordinatorState: () => void;
  readonly realEstateOffice: RealEstateOfficeViewModel;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly isModal: boolean;
  readonly entityId?: number;
  readonly saving?: boolean;
  readonly txCoordinator?: TransactionCoordinatorViewModel;
}

// Component definition
export class TransactionCoordinatorComponent extends React.Component<TransactionCoordinatorComponentProperties> {
  componentWillMount() {
    if (this.props.match && this.props.match.params.id) {
      this.props.getTransactionCoordinator(this.props.match.params.id);
    } else if (this.props.entityId && !this.props.txCoordinator) {
      this.props.getTransactionCoordinator(this.props.entityId);
    }
  }

  componentWillUnmount() {
    this.props.clearTransactionCoordinatorState();
  }

  renderError() {
    if (!this.props.message) {
      return <div />;
    }
    return <div className="error">{this.props.message}</div>;
  }

  getSelectValue(event: Option<OfficeSearchViewModel>): number {
    if (typeof event === 'number') {
      return event;
    }
    return event.id;
  }

  renderOfficeSearch(suggestion: Option<OfficeSearchViewModel>) {
    return (
      <div>
        <div>Name: {suggestion.name ? suggestion.name : '—'}</div>
        <div>Address: {suggestion.address ? suggestion.address : '—'}</div>
        <div>City: {suggestion.city ? suggestion.city : '—'}</div>
        <div>Phone #: {suggestion.phone ? suggestion.phone : '—'}</div>
      </div>
    );
  }

  setDefaultOffice() {
    if (this.props.realEstateOffice) {
      return {
        id: this.props.realEstateOffice.id,
        name: this.props.realEstateOffice.name,
        phone: this.props.realEstateOffice.phone
      };
    } else {
      return { id: -1, name: 'Select one...', phone: '' };
    }
  }

  renderSave() {
    if (this.props.isModal) {
      return (
        <div className="row">
          <div className="col-xs-2">
            <button className="btn btn-hidden" disabled={this.props.saving} type="submit" ref={this.props.buttonRef}>
              {this.props.saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="row end-xs text-right mt-10 mb-10">
        <button className="btn btn-secondary" type="button" onClick={() => window.history.back()}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={this.props.saving} type="submit" ref={this.props.buttonRef}>
          {this.props.saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    );
  }

  render() {
    return (
      <div>
        {this.props.isModal ? null : <Breadcrumbs />}
        <h2>Transaction Coordinator</h2>
        <FormWrapper
          model="transactionCoordinatorForm"
          className="form-horizontal"
          onSubmit={(a: any) => this.props.onSaveClick(a)}
          hideNativeErrors={true}
          shouldFormReset={true}
        >
          <div className="row">
            <div className="form-control col-md-6">
              <label htmlFor="transactionCoordinatorForm.name">Name</label>
              <Control.text model=".name" validators={{ required: hasValue, hasAlpha: hasAlpha }} maxLength={32} />
              <Errors
                className="error"
                model=".name"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-6">
              <label htmlFor="transactionCoordinatorForm.emailAddress">Email Address</label>
              <Control.text
                model=".emailAddress"
                validators={{ required: hasValue, isEmail: isValidEmail }}
                maxLength={50}
              />
              <Errors
                className="error"
                model=".emailAddress"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-6">
              <label htmlFor="transactionCoordinatorForm.officeId">Office</label>
              <Control
                model=".officeId"
                component={(props: { value: ''; onChange: Function }) => {
                  return (
                    <TypeAheadComponent
                      {...props}
                      renderValue={this.renderOfficeSearch}
                      onChange={props.onChange}
                      valueKey={'id'}
                      labelKey={'name'}
                      getSuggestions={this.props.officeLookup}
                      value={props.value}
                    />
                  );
                }}
              />
              <Errors
                className="error"
                model=".officeId"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="col-xs-12 mb-10 pb-10">
              <CheckboxInline>
                <Control.checkbox model=".onlineOrders" />
                <label className="checkbox-inline" htmlFor="transactionCoordinatorForm.onlineOrders">
                  Online Orders
                </label>
              </CheckboxInline>
            </div>
            <div className="col-xs-12">
              <label>Contact Preference</label>
              <div className="row mt-10 mr-5">
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".usps" />
                    <label className="checkbox-inline" htmlFor="transactionCoordinatorForm.usps">
                      USPS
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".fax" />
                    <label className="checkbox-inline" htmlFor="transactionCoordinatorForm.fax">
                      Fax
                    </label>
                  </CheckboxInline>
                </div>
                <div className="col-md-3">
                  <CheckboxInline>
                    <Control.checkbox model=".email" />
                    <label className="checkbox-inline" htmlFor="transactionCoordinatorForm.email">
                      Email
                    </label>
                  </CheckboxInline>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-3">
              <label htmlFor="transactionCoordinatorForm.transCoordType.id">Type</label>
              <Select
                model=".transCoordType.id"
                identifier={DynamicSelectIdentifier.TransactionCoordinatorTypes}
                validators={{ required: hasValue }}
              />
              <Errors
                className="error"
                model=".transCoordType.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="transactionCoordinatorForm.transCoordStatus.id">Status</label>
              <Select
                model=".transCoordStatus.id"
                identifier={DynamicSelectIdentifier.TransactionCoordinatorStatuses}
                validators={{ required: hasValue }}
                disabled={!Role.CheckRoles([Roles.Admin, Roles.IBSAdmin, Roles.IBSManager])}
              />
              <Errors
                className="error"
                model=".transCoordStatus.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="transactionCoordinatorForm.directPhone">Direct Phone</label>
              <Control.text
                model=".directPhone"
                parser={parsePhoneNumber}
                validators={{ required: hasValue, isPhone: isValidPhoneNumber }}
                maxLength={12}
              />
              <Errors
                className="error"
                model=".directPhone"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="transactionCoordinatorForm.directFax">Direct Fax</label>
              <Control.text
                model=".directFax"
                parser={parsePhoneNumber}
                validators={{ isFax: isValidPhoneNumber }}
                maxLength={12}
              />
              <Errors
                className="error"
                model=".directFax"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <h3>Override Address</h3>
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-6">
              <label htmlFor="transactionCoordinatorForm.address.line1">Address 1</label>
              <Control.text model=".address.line1" validators={{ hasAlpha: hasAlpha, hasNumber }} maxLength={32} />
              <Errors
                className="error"
                model=".address.line1"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-6">
              <label htmlFor="transactionCoordinatorForm.address.line2">Address 2 (optional)</label>
              <Control.text model=".address.line2" maxLength={32} />
              <Errors
                className="error"
                model=".address.line2"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-control col-md-6">
              <label htmlFor="transactionCoordinatorForm.address.city">City</label>
              <Control.text model=".address.city" validators={{ isValidCity, hasAlpha: hasAlpha }} maxLength={28} />
              <Errors
                className="error"
                model=".address.city"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-4">
              <label htmlFor="transactionCoordinatorForm.address.state.abbreviation">State</label>
              <Select model=".address.state.abbreviation" identifier={DynamicSelectIdentifier.USStates} />
              <Errors
                className="error"
                model=".address.state.abbreviation"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="form-control col-md-2">
              <label htmlFor="transactionCoordinatorForm.address.zip">Zip</label>
              <Control.text model=".address.zip" validators={{ isValidZip: isValidZip }} />
              <Errors
                className="error"
                model=".address.zip"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-md-9">
              <Errors
                className="error"
                model="transactionCoordinatorForm"
                show={true}
                messages={getDefaultErrorMessages({
                  hasCity: 'Enter City',
                  hasState: 'Enter State',
                  hasZip: 'Enter Zip'
                })}
              />
              {/* <div>{this.renderError()}</div> */}
            </div>
          </div>
          {this.renderSave()}
        </FormWrapper>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/transaction-coordinator/_components/transaction-coordinator/transaction-coordinator.component.tsx